import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { isEmpty } from '@fcom/core/utils/utils';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { LanguageService } from '@fcom/ui-translate/services/language.service';

import { ScrollHandleContainerComponent } from '../scroll-handle-container/scroll-handle-container.component';
import { Direction } from '../../interfaces/utils.interface';
import { HistogramBar } from '../../interfaces/histogram.interface';
import { MediaQueryService } from '../../services/media-query/media-query.service';
import { generateBarsForYear } from '../../utils/histogram.utils';

const MIN_COLUMN_HEIGHT = 44;
const HISTOGRAM_TOP_SPACING = 20;

@Component({
  selector: 'fin-histogram',
  templateUrl: './histogram.component.html',
  styleUrls: ['./histogram.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HistogramComponent implements AfterViewInit {
  private mediaQueryService = inject(MediaQueryService);
  private languageService = inject(LanguageService);

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly CMS_STYLE_OVERRIDE = {
    'margin-top': '0px',
  };

  barData = input<HistogramBar[]>([]);
  selected = input<number | null>(0);
  isActive = input(false);
  isLoading = input(false);
  readonly noSnippet = input(false);

  barSelected = output<HistogramBar>();
  barsScrolled = output<Direction>();

  histogramContainer = viewChild<ElementRef>('histogramContainer');
  scrollContainer = viewChild<ScrollHandleContainerComponent>('scrollContainer');
  bars = viewChildren<ElementRef>('bar');

  private dateTranslations = toSignal(this.languageService.translate<Record<string, unknown>>('date'));

  private loadingBars = computed<HistogramBar[]>(() => {
    const dateTranslations = this.dateTranslations();
    if (!dateTranslations) {
      return [];
    }
    return generateBarsForYear(LocalDate.now(), dateTranslations).map((bar) => ({
      ...bar,
      height: '20%',
      isCheapest: false,
      amount: '',
      noFlight: false,
      currencyCode: '',
    }));
  });

  minAmount = computed(() => {
    const barData = this.barData();
    return barData && Math.min(...barData.filter((bar) => +bar.amount && +bar.amount > 0).map((bar) => +bar.amount));
  });

  private maxAmount = computed(() => {
    const barData = this.barData();
    return barData && Math.max(...barData.filter((bar) => +bar.amount && +bar.amount > 0).map((bar) => +bar.amount));
  });

  range = computed<HistogramBar[]>(() => {
    if (this.isLoading()) {
      return this.loadingBars();
    }

    const barData = this.barData();
    if (!this.isActive() || isEmpty(barData)) {
      return [];
    }

    // Re-measure bar heights whenever the viewport is resized.
    this.mediaQueryService.viewportSize();

    const minAmount = this.minAmount();
    const maxAmount = this.maxAmount();
    return barData.map((bar) => ({
      ...bar,
      height: `${this.calculateHeightBar(bar, minAmount, maxAmount)}px`,
      isCheapest: +bar.amount === minAmount && !bar.noFlight,
    }));
  });

  constructor() {
    effect(() => {
      const value = this.selected();
      untracked(() => {
        const scrollContainer = this.scrollContainer();
        if (scrollContainer && value != null) {
          scrollContainer.scrollToElement(this.bars()[value]);
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.scrollContainer()?.updateScrollHandles();
  }

  /**
   * Displays bar center based on container
   * @param selectedBar the bar to display centered
   */
  scrollToBarByIndex(selectedBar: HistogramBar): void {
    this.scrollContainer()?.scrollToElement(this.bars()[selectedBar.index]);
    this.barSelected.emit(selectedBar);
  }

  private calculateHeightBar(bar: HistogramBar, minAmount: number, maxAmount: number): number {
    const maxColumnHeight = this.histogramContainer()?.nativeElement?.offsetHeight - HISTOGRAM_TOP_SPACING;
    if (minAmount === maxAmount) {
      return MIN_COLUMN_HEIGHT;
    }

    if (+bar.amount === maxAmount) {
      return maxColumnHeight;
    }

    const percentOfAmount = ((+bar.amount - minAmount) / (maxAmount - minAmount)) * 100;
    const extraHeightInPx = ((maxColumnHeight - MIN_COLUMN_HEIGHT) * percentOfAmount) / 100;

    return MIN_COLUMN_HEIGHT + extraHeightInPx;
  }
}
